import { ForwardedRef, forwardRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { RoutePaths } from '../../../../constants';
import { PatientListItemDetails } from '../PatientListItemDetails/PatientListItemDetails';
import { PatientTask } from '../PatientTask/PatientTask';
import styles from './PatientListItem.module.scss';
import cx from 'classnames';
import { Icon, MenuButton, Text } from 'components';
import { appActions } from 'store/app/slice';
import { selectCurrentPatientCase } from 'store/patients/selectors';
import { patientsActions } from 'store/patients/slice';
import { getUsers } from 'store/users/selectors';
import { useDispatch, useSelector } from 'store/utils';
import { Patient as PatientType } from 'types/patientCase';
import { calculateAge } from 'utils';
import { getFormattedDate } from 'utils/getFormattedData';

type Props = PatientType & {
  className?: string;
  markSelected?: boolean;
  showControls?: boolean;
  withPersistentMessageIcon?: boolean;
};

export const PatientListItem = forwardRef(
  (
    {
      id,
      clinicId,
      task,
      taskStatusUpdatedAt,
      assignedDoctorId,
      externalPatientId,
      firstName,
      lastName,
      oralHealthTherapist,
      isTaskBlocked,
      isUnreadMessageExists,
      className,
      dateOfBirth,
      markSelected = true,
      showControls = false,
      withPersistentMessageIcon = false,
    }: Props,
    ref: ForwardedRef<HTMLDivElement>,
  ) => {
    const dispatch = useDispatch();

    const navigate = useNavigate();

    const location = useLocation();

    const currentPatientCase = useSelector(selectCurrentPatientCase);

    const formattedDate = getFormattedDate(taskStatusUpdatedAt);

    const users = useSelector(getUsers);

    const showMessageIcon = withPersistentMessageIcon || isUnreadMessageExists;

    const setCurrentPatientCaseHandler = () => {
      dispatch(appActions.setShowPatientCaseList(false));
      dispatch(appActions.setIsModalOpened({ name: 'activityFeed', isOpened: false }));
      dispatch(appActions.setCurrentPatientTab('information'));
      dispatch(patientsActions.setPatientMessageFilters({}));

      navigate(`${RoutePaths.PATIENTS}/${id}`, {
        state: {
          path: location.pathname,
        },
      });
    };

    const clinicName = users.find((clinic) => clinic.id === clinicId)?.name;
    const doctorName = users.find((doctor) => doctor.id === assignedDoctorId)?.name;

    const age = `${calculateAge(dateOfBirth)} y.o.`;

    return (
      <div
        className={cx(styles.wrapper, { [styles.isSelected]: currentPatientCase?.id === id && markSelected }, [
          className,
        ])}
        onClick={setCurrentPatientCaseHandler}
        ref={ref}>
        <div className={styles.cardHeader}>
          <div className={styles.headerBlock}>
            <Text type="h5">{externalPatientId}</Text>
            <Text type="body-big" className={styles.patientName}>
              {firstName} {lastName}
            </Text>
            {showControls && <PatientListItemDetails patientCaseId={id} />}
          </div>
          <Text type="caption" className={styles.headerAge}>
            {age}
          </Text>
        </div>

        <div className={styles.stageBlock}>
          <PatientTask task={isTaskBlocked ? 'BLOCKED' : task} />
          {showMessageIcon && (
            <div className={styles.messageIconWrapper}>
              <Icon name="Message" />
            </div>
          )}
        </div>
        <div className={styles.blockWrapper}>
          <div className={styles.topBlock}>
            <div className={styles.infoBlock}>
              <Icon name="LastUpdate" className={styles.icon} />
              <Text type="caption">{formattedDate}</Text>
            </div>

            <div className={styles.infoBlock}>
              <Icon name="Location" className={styles.icon} />
              <Text type="caption">{clinicName}</Text>
            </div>
          </div>
          <div className={styles.bottomBlock}>
            <div className={styles.infoBlock}>
              <Icon name="Diagnosis" className={styles.icon} />
              <Text type="caption">{oralHealthTherapist}</Text>
            </div>
            {doctorName && (
              <div className={styles.infoBlock}>
                <Icon name="Tooth" className={styles.icon} />
                <Text type="caption">{doctorName}</Text>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  },
);

PatientListItem.displayName = 'PatientListItem';
